import { useState, useRef, forwardRef, useEffect, useContext } from 'react';
import csc from "countrycitystatejson";
import { useLocation, useNavigate } from 'react-router-dom';
import { FaTimes } from "react-icons/fa";
import Select from 'react-select';
import Search from '../Components/Images/search-lg.svg';
import Upload from '../Components/Images/upload.png';
import { Context } from '../Context/Context';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import CountryCode from './CountryCode';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import imageCompression from "browser-image-compression";

const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <input
        type="text"
        className="border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none"
        onClick={onClick}
        value={value}
        ref={ref}
        placeholder={placeholder}
        readOnly
    />
));

const VerifyAddress = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [document, setDocument] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const intervalRef = useRef(null);
    const [validationError, setValidationError] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCode, setSelectedCode] = useState(CountryCode[122]);
    const { setForms } = useContext(Context);

    // Countries / states / cities data + selects
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]); // array of state objects
    const [cities, setCities] = useState([]); // array of city strings

    // react-select option objects
    const [selectedCountry, setSelectedCountry] = useState(null); // option { value: shortName, name: countryName, label: JSX, raw }
    const [selectedState, setSelectedState] = useState(null);     // option { value: stateName, label, raw }
    const [selectedCity, setSelectedCity] = useState(null);       // option { value: cityName, label }
    const [selectedCitizenship, setSelectedCitizenship] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        date_of_birth: '',
        country: '',
        country_code: '',
        state_or_province: '',
        postal_code: '',
        proof_of_address_type: 'UTILITY_BILL',
        street_line_1: '',
        street_line_2: '',
        city: '',
        citizenship: '',
        phone_number: '',
        phone_country_code: ''
    });

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const toggleDropdown = () => setIsOpen(!isOpen);
    const handleSelect = (country) => {
        setSelectedCode(country);
        setIsOpen(false);
        setFormData((prev) => ({ ...prev, phone_country_code: country.name }));
    };

    const validateForm = () => {
        const phone_NumberRegex = /^.{10,}$/;
        let error = {};
        if (!phone_NumberRegex.test(formData.phone_number)) {
            error.phone_number = 'Phone number must contain a minimum of 10 digits.';
        }
        setValidationError(error);
        return Object.keys(error).length === 0;
    };

    const handleDateChange = (date, fieldName) => {
        if (!date) return;
        const formattedDate = format(date, 'yyyy-MM-dd');
        setFormData(prev => ({ ...prev, [fieldName]: formattedDate }));
    };

    const formatPhoneNumber = () => {
        const raw = (formData.phone_number || '').trim();
        const cleaned = raw.startsWith('0') ? raw.slice(1) : raw;
        return `${selectedCode.code}${cleaned}`;
    };

    // sync phone country code to localStorage
    useEffect(() => {
        const prevData = JSON.parse(localStorage.getItem('KYC-Data')) || {};
        const updatedData = { ...prevData, phone_country_code: selectedCode.name };
        localStorage.setItem('KYC-Data', JSON.stringify(updatedData));
        setFormData((prev) => ({ ...prev, phone_country_code: selectedCode.name }));
    }, [selectedCode]);

    // Load countries on mount (from the package)
    useEffect(() => {
        try {
            const allCountries = csc.getCountries() || [];
            // optional: sort alphabetically
            allCountries.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setCountries(allCountries);
        } catch (err) {
            console.error("Error loading countries from countrycitystatejson:", err);
            setCountries([]);
        }
    }, []);

    // Helper: build consistent react-select option objects
    const buildCountryOption = (c) => {
        // take the library's shortName if available; fallback to other keys or 'xx'
        const codeRaw = (c && (c.shortName || c.sortname || c.isoCode || c.iso2 || c.code)) || 'xx';
        const code = (codeRaw || 'xx').toString().toLowerCase();

        return {
            label: (
                <div className="flex items-center gap-2">
                    <img
                        src={`https://flagcdn.com/w40/${code}.png`}
                        alt={c?.name || 'Unknown'}
                        className="w-5 h-4 rounded-sm object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://flagcdn.com/w40/xx.png'; }}
                    />
                    <span>{c?.name || 'Unknown'}</span>
                </div>
            ),
            value: (c && (c.shortName || c.sortname || c.isoCode || c.iso2 || c.code)) || '',
            name: c?.name || '',
            raw: c
        };
    };

    const buildStateOption = (s) => ({
        label: (s && s.name) ? s.name : String(s || ''),
        value: (s && s.name) ? s.name : String(s || ''),
        raw: s
    });

    const buildCityOption = (cityStr) => ({
        label: cityStr,
        value: cityStr
    });

    // Options arrays fed to react-select (memoization not necessary but can be added)
    const countryOptions = (countries || []).map(buildCountryOption);
    const stateOptions = (states || []).map(buildStateOption);
    const cityOptions = (cities || []).map(buildCityOption);

    useEffect(() => {
        const KYC_Data = JSON.parse(localStorage.getItem("KYC-Data"));
        const ProofOfAddress = JSON.parse(localStorage.getItem("ProofOfAddress"));

        if (KYC_Data) {
            setFormData(KYC_Data);

            // Restore Country
            const allCountries = csc.getCountries() || [];
            const country = allCountries.find(
                c =>
                    c.name.toLowerCase() === KYC_Data.country?.toLowerCase() ||
                    c.shortName?.toLowerCase() === KYC_Data.country_code?.toLowerCase()
            );

            if (country) {
                const countryOption = buildCountryOption(country);
                setSelectedCountry(countryOption);
                setSelectedCitizenship(countryOption);

                const fetchedStates =
                    csc.getStatesByShort(country.shortName?.toUpperCase()) || [];
                setStates(fetchedStates);

                // Restore State
                const stateObj = fetchedStates.find(
                    s => s.name.toLowerCase() === KYC_Data.state_or_province?.toLowerCase()
                );

                if (stateObj) {
                    const stateOpt = buildStateOption(stateObj);
                    setSelectedState(stateOpt);
                    setFormData(prev => ({
                        ...prev,
                        state_or_province: stateObj.name,
                    }));

                    // Restore City
                    const fetchedCities =
                        csc.getCities(country.shortName, stateObj.name) || [];
                    setCities(fetchedCities);

                    const matchedCity = fetchedCities.find(
                        c => c.toLowerCase() === KYC_Data.city?.toLowerCase()
                    );

                    if (matchedCity) {
                        setSelectedCity(buildCityOption(matchedCity));
                    } else if (KYC_Data.city) {
                        // fallback for unmatched city names
                        setSelectedCity(buildCityOption(KYC_Data.city));
                    }
                }
            }

            // Restore separate citizenship (if different)
            if (
                KYC_Data.citizenship &&
                (!KYC_Data.country || KYC_Data.citizenship !== KYC_Data.country)
            ) {
                const citizenCountry = allCountries.find(
                    c => c.name.toLowerCase() === KYC_Data.citizenship.toLowerCase()
                );
                if (citizenCountry) {
                    setSelectedCitizenship(buildCountryOption(citizenCountry));
                } else {
                    setSelectedCitizenship({
                        label: KYC_Data.citizenship,
                        value: KYC_Data.citizenship,
                        name: KYC_Data.citizenship,
                    });
                }
            }
        }

        // Restore Proof of Address
        if (ProofOfAddress) {
            setDocument(ProofOfAddress.name);
            const byteArray = new Uint8Array(ProofOfAddress.data);
            const file = new File([byteArray], ProofOfAddress.name, {
                type: ProofOfAddress.type,
            });
            setForms(file);
        }
    }, [setForms]);

    // Handle country change (Country of residence)
    const handleCountryChange = (option) => {
        if (!option) return;
        setSelectedCountry(option);

        // also prefill citizenship to same country (keeps old behavior)
        setSelectedCitizenship(option);

        setFormData((prev) => ({
            ...prev,
            country: option.name,
            country_code: option.value,
            citizenship: option.name,
        }));

        // load states for the selected country shortName (option.value)
        const shortName = (option.value || '').toString().toUpperCase();
        const fetchedStates = csc.getStatesByShort(shortName) || [];
        setStates(fetchedStates);
        setSelectedState(null);
        setCities([]);
        setSelectedCity(null);
        setFormData((prev) => ({ ...prev, state_or_province: "", city: "" }));
    };

    // put this near your other handlers
    const handleCitizenshipChange = (option) => {
        if (!option) {
            setSelectedCitizenship(null);
            setFormData(prev => ({ ...prev, citizenship: '' }));
            return;
        }

        // update citizenship select visually & in formData
        setSelectedCitizenship(option);
        setFormData(prev => ({ ...prev, citizenship: option.name }));

        handleCountryChange(option);
    };


    // Handle state change
    const handleStateChange = (option) => {
        if (!option || !selectedCountry) {
            setSelectedState(option || null);
            setCities([]);
            setSelectedCity(null);
            setFormData(prev => ({ ...prev, state_or_province: '', city: '' }));
            return;
        }

        setSelectedState(option);
        setFormData((prev) => ({ ...prev, state_or_province: option.value }));

        // get cities: API signature is getCities(countryShortName, stateName)
        const countryShort = (selectedCountry.value || '').toString().toUpperCase();
        const fetchedCities = csc.getCities(countryShort, option.value) || [];
        setCities(fetchedCities);
        setSelectedCity(null);
        setFormData(prev => ({ ...prev, city: '' }));
    };

    // Handle city change
    const handleCityChange = (option) => {
        setSelectedCity(option);
        setFormData(prev => ({ ...prev, city: option ? option.value : '' }));
    };

    // Submit logic (kept yours, minor robustness)
    const handleSubmit = (e) => {
        e.preventDefault();

        // basic check
        if (
            !formData.country ||
            !formData.state_or_province ||
            !formData.city ||
            !formData.street_line_1 ||
            !formData.street_line_2 ||
            !formData.postal_code ||
            !document ||
            !formData.date_of_birth ||
            !formData.citizenship ||
            !formData.phone_number
        ) {
            alert('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        validateForm();

        const prevData = JSON.parse(localStorage.getItem('KYC-Data')) || {};
        const updatedData = {
            ...prevData,
            ...formData,
            proof_of_address_type: formData.proof_of_address_type || "UTILITY_BILL",
            phone_number: formatPhoneNumber(),
        };
        localStorage.setItem('KYC-Data', JSON.stringify(updatedData));

        // simulate progress to next step
        setTimeout(() => {
            setIsSubmitting(false);
            navigate('/dashboard/uploadDocument');
        }, 2500);
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 2700);
    };

    // File handling (kept your logic)
    const simulateProgress = () => {
        setUploading(true);
        setProgress(0);
        intervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(intervalRef.current);
                    return 100;
                }
                return prev + 5;
            });
        }, 100);
    };

    const handleDocumentChange = async (e) => {
        const selected = e.target.files[0];
        if (selected) {
            setDocument(selected.name);
            simulateProgress();
            try {
                let finalFile = selected;
                const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
                if (!allowedTypes.includes(selected.type)) {
                    alert("Only JPG, PNG, or PDF files are allowed");
                    return;
                }
                if (selected.type !== "application/pdf" && selected.size > 2 * 1024 * 1024) {
                    const options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true };
                    finalFile = await imageCompression(selected, options);
                }
                setForms(finalFile);
            } catch (err) {
                console.error("Compression error:", err);
                setForms(selected);
            }
        }
    };

    const handleCancel = () => {
        clearInterval(intervalRef.current);
        setDocument(null);
        setProgress(0);
        setUploading(false);
        setForms(null);
    };

    useEffect(() => {
        if (progress === 100) setUploading(false);
    }, [progress]);

    // react-select custom styles (kept as you had)
    const customStyles = {
        indicatorSeparator: () => ({
            display: 'none',
        }),
        control: (base) => ({
            ...base,
            borderColor: '#F3F0F7',
            paddingTop: '5px',
            paddingBottom: '5px',
            paddingRight: '7px',
            paddingLeft: '7px',
            boxShadow: '0 1px 2px 0 #1D1C1F0D',
            borderRadius: '8px',
            borderWidth: '1.2px'
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
            color: 'black',
            padding: 10,
            cursor: 'pointer',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            display: 'none'
        }),
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col max-w-[560px] mx-auto md:p-10 py-10 px-4">
            <div>
                <h1 className='text-[28px] font-medium text-[#1D1C1F] text-center'>Verify Your Basic Details</h1>
                <p className='text-[#525154] text-[14px] font-normal text-center'>
                    Enter your details to verify your account
                </p>
            </div>
            <div className='flex items-center justify-center gap-2 mt-5 transition-all duration-300'>
                <p className={location.pathname === '/dashboard/verifyidentity' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p>
                {/* <p className={location.pathname === '/dashboard/verifyidentity' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p> */}
                <p className={location.pathname === '/dashboard/uploadDocument' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p>
            </div>

            <div className='mt-12 flex flex-col gap-6 text-[#525154] font-medium text-[14px]'>
                {/* Country */}
                <div>
                    <label>Country of Residence <span className='text-red-400'>*</span></label>
                    <div className='flex items-center mt-1'>
                        <Select
                            styles={customStyles}
                            options={countryOptions}
                            onChange={handleCountryChange}
                            placeholder="Select Country"
                            value={selectedCountry}
                            className="w-full"
                            menuPlacement="auto"
                            isSearchable
                            filterOption={(option, inputValue) =>
                                option.data.name.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        />
                        <img className='ml-[-29px] z-20' src={Search} alt="" />
                    </div>
                </div>

                {/* State / Province */}
                <div>
                    <label>State / Province <span className='text-red-400'>*</span></label>
                    <div className='flex items-center mt-1'>
                        <Select
                            styles={customStyles}
                            options={stateOptions}
                            onChange={handleStateChange}
                            placeholder="Select State"
                            value={selectedState}
                            className="w-full"
                            isSearchable
                            isDisabled={!stateOptions.length}
                            menuPlacement="auto"
                        />
                        <img className='ml-[-29px] z-20' src={Search} alt="" />
                    </div>
                </div>

                {/* City */}
                <div>
                    <label>City <span className='text-red-400'>*</span></label>
                    <Select
                        styles={customStyles}
                        options={cityOptions}
                        onChange={handleCityChange}
                        placeholder="Select City"
                        value={selectedCity}
                        className="w-full"
                        isDisabled={!cityOptions.length}
                        menuPlacement="auto"
                    />
                </div>

                <div>
                    <label>Citizenship <span className='text-red-400'>*</span></label>
                    <div className='flex items-center mt-1'>
                        <Select
                            styles={customStyles}
                            options={countryOptions}
                            onChange={handleCitizenshipChange}
                            placeholder="Select Country of Citizenship"
                            value={selectedCitizenship || selectedCountry}
                            className="w-full"
                            menuPlacement="auto"
                            isSearchable
                            filterOption={(option, inputValue) =>
                                (option.data?.name || '').toLowerCase().includes(inputValue.toLowerCase())
                            }
                        />
                        <img className='ml-[-29px] z-20' src={Search} alt="" />
                    </div>
                </div>

                {/* date picker, phone, address fields, upload etc. (kept your UI) */}
                <div className='flex flex-col'>
                    <label>Date of birth <span className='text-red-400'>*</span></label>
                    <DatePicker
                        selected={formData.date_of_birth ? new Date(formData.date_of_birth.replace(/\//g, '-')) : null}
                        onChange={(date) => handleDateChange(date, 'date_of_birth')}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="YYYY-MM-DD"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        showMonthDropdown
                        dropdownMode="select"
                        customInput={<CustomInput />}
                        popperPlacement="bottom"
                    />
                </div>

                <div>
                    <label>Phone Number <span className='text-red-400'>*</span></label>
                    <div className='mt-1 relative'>
                        <input
                            className='border-[1.2px] border-[#D2D0D6] shadow-custom-light rounded-md placeholder-black/4- w-full pl-[110px] p-2 outline-none'
                            type="text"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            maxLength="11"
                            inputMode="numeric"
                            placeholder='9000000000'
                        />
                        <div className="absolute top-[5px] left-1 w-[120px]">
                            <div className="flex items-center gap-3 p-1 border-none rounded cursor-pointer bg-transparent" onClick={toggleDropdown}>
                                {selectedCode.name} ({selectedCode.code}) {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                            </div>

                            {isOpen && (
                                <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-md max-h-60 overflow-y-auto">
                                    {CountryCode.map((country, index) => (
                                        <div key={index} onClick={() => handleSelect(country)} className="p-2 hover:bg-gray-100 cursor-pointer">
                                            {country.name} ({country.code})
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {validationError.phone_number && <p className='text-red-500 text-[14px]'>{validationError.phone_number}</p>}
                    </div>
                </div>

                <div>
                    <label>Street Line 1 <span className='text-red-400'>*</span></label>
                    <input className='border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none'
                        type="text" name="street_line_1" value={formData.street_line_1} onChange={handleChange} placeholder='564866' />
                </div>

                <div>
                    <label>Street Line 2 <span className='text-red-400'>*</span></label>
                    <input className='border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none'
                        type="text" name="street_line_2" value={formData.street_line_2} onChange={handleChange} placeholder='564866' />
                </div>

                <div>
                    <label>Zip / postal code <span className='text-red-400'>*</span></label>
                    <input className='border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none'
                        type="number" name="postal_code" value={formData.postal_code} onChange={handleChange} placeholder='564866' />
                </div>

                <div>
                    <label>Proof of Address (Utility bill or Bank Statement) <span className='text-red-400'>*</span></label>
                    <div className='mt-1 relative'>
                        <label htmlFor="upload">
                            <div className='border-[1.2px] cursor-pointer border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none flex gap-2 items-center justify-center'>
                                <div className='flex gap-2 items-center'>
                                    <img src={Upload} alt="" />
                                    {document ? <p>{document}</p> : <p>Upload document</p>}
                                </div>
                            </div>
                            <input type="file" id="upload" onChange={handleDocumentChange} hidden />
                        </label>

                        {document ? (
                            <FaTimes className='absolute top-[35%] right-2 text-black/30 cursor-pointer' onClick={() => setDocument('')} />
                        ) : null}
                    </div>

                    {uploading && (
                        <div className='flex items-center justify-center gap-5 mt-4'>
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div className="bg-[#411c87] h-3 transition-all duration-200" style={{ width: `${progress}%` }} />
                            </div>
                            <FaTimes className='mt-[-2px]' onClick={handleCancel} />
                        </div>
                    )}
                </div>

                <div className='flex gap-2 mt-4'>
                    <p className='p-3 rounded-lg w-[25%] cursor-pointer text-center border-[1.2px] border-black/10' onClick={() => { navigate('/dashboard/getready'); window.scrollTo(0, 0); }}>
                        Previous
                    </p>
                    <button type="submit" className={`p-[10px] px-4 rounded-lg text-white w-[80%] ${isSubmitting ? 'bg-[#E8E1F5]' : 'bg-[#531CB3]'}`} disabled={isSubmitting}>
                        {isSubmitting ? 'Loading...' : 'Continue'}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default VerifyAddress;
